/**
 * 대시보드 API 클라이언트 — backend /dashboard/* 와 1:1.
 */

import { api } from './api';

/** 이번달 매출·이익 구성 (v2.78.0) — 카드 클릭 시 「무엇이 얼마인지」 */
export interface DashboardMonthlyPart {
  key: 'sales' | 'maintenance' | 'bell';
  label: string;
  revenue: number;
  cost: number;
}

export interface DashboardMetrics {
  monthlyRevenue: number | null;     // viewer 마스킹 시 null
  monthlyProfit: number | null;      // viewer 마스킹 시 null
  /** 매출·이익 카드를 누르면 보여줄 세 갈래. 합계와 같은 질의에서 나온다. viewer 는 null */
  monthlyParts: DashboardMonthlyPart[] | null;
  activeSales: { count: number; totalAmount: number | null };
  pendingInvoice: { count: number; totalAmount: number | null } | null; // viewer 자체 null
}

export interface DashboardStage {
  name: string;
  count: number;
  totalAmount: number | null;
}

export interface DashboardMonthlyComparison {
  month: number;
  current: number | null;
  previous: number | null;
  previous2: number | null;
}

export interface DashboardUpcomingDelivery {
  salesId: string;
  endUser: string | null;
  projectName: string | null;
  deliveryDate: string;
  daysLeft: number;
}

/** 대시보드 재고표 한 줄 — 수량 칸은 재고관리 화면과 같은 `v_stock` 칸 (v2.182.0) */
export interface DashboardTopStockItem {
  itemCode: string;
  itemName: string | null;
  currentStock: number;
  pendingDelivery: number;
  incoming: number;
  leased: number;
  availableStock: number;
  safeStock: number | null;
  isLowStock: boolean;
}

export interface DashboardSummary {
  asOf: string;
  metrics: DashboardMetrics;
  stages: DashboardStage[];
  monthlyComparison: DashboardMonthlyComparison[];
  upcomingDeliveries: DashboardUpcomingDelivery[];
  topStock: DashboardTopStockItem[];
}

export async function getSummary(): Promise<DashboardSummary> {
  return api.get('/dashboard/summary');
}

export interface DashboardPendingInvoice {
  salesId: string;
  endUser: string | null;
  proposalCompany: string | null;
  projectName: string | null;
  status: string;
  amount: number | null;   // viewer 마스킹 시 null
  contractDate: string | null;
}

/** 계산서 미발행 목록 — 알림 팝업 상세 */
export async function getPendingInvoices(): Promise<DashboardPendingInvoice[]> {
  return api.get('/dashboard/pending-invoices');
}

// ── 일일 변동 현황 (admin/mgmt 전용) ─────────────────────────

export interface DailyChangeBucket {
  name: string;
  before: number;
  after: number;
  delta: number;
}

export interface DailyChangeFieldChange {
  label: string;
  old: string;
  new: string;
}

export interface DailySalesChangeDetail {
  salesId: string;
  endUser: string | null;
  projectName: string | null;
  changeType: '신규' | '수정' | '삭제';
  changes: DailyChangeFieldChange[];
  amountDelta: number;
  users: string[];
}

export interface DailyMaintChangeDetail {
  maintenanceId: string;
  name: string | null;
  endUser: string | null;
  ym: string;
  changeType: '신규' | '수정' | '삭제';
  changes: DailyChangeFieldChange[];
  amountDelta: number;
  users: string[];
}

export interface DailyStockChangeDetail {
  itemCode: string;
  itemName: string | null;
  isKeyStock: boolean;
  before: number;
  after: number;
  delta: number;
  users: string[];
}

export interface DailyChangesData {
  ready: boolean;
  reason?: string;
  firstDate?: string | null;
  date: string;
  /** 합계(버킷) 집계 기준 연도 = 조회한 날짜의 연도 (영업=일련번호 연도·유지보수=귀속연도) */
  bucketYear?: number;
  periodFrom?: string;
  periodTo?: string;
  periodToLive?: boolean;
  spanDays?: number;
  sales?: { buckets: DailyChangeBucket[]; details: DailySalesChangeDetail[] };
  maint?: { buckets: DailyChangeBucket[]; details: DailyMaintChangeDetail[] };
  stock?: {
    totalBefore: number;
    totalAfter: number;
    delta: number;
    details: DailyStockChangeDetail[];
  };
}

export async function getDailyChanges(date: string): Promise<DailyChangesData> {
  return api.get(`/dashboard/daily-changes?date=${encodeURIComponent(date)}`);
}

/** 일일 변동 엑셀 다운로드 — blob + Content-Disposition 파일명 */
export async function downloadDailyChangesExcel(date: string): Promise<void> {
  const res = await fetch(
    `/api/dashboard/daily-changes/export.xlsx?date=${encodeURIComponent(date)}`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      if (json?.error?.message) msg = json.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = cd.match(/filename="([^"]+)"/i);
  a.download = utf8Match
    ? decodeURIComponent(utf8Match[1]!)
    : asciiMatch
      ? asciiMatch[1]!
      : `daily_changes_${date}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
